<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import MarkdownIt from 'markdown-it'
import { NButton, NEmpty, NInput, NSelect, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import { useOutputSkillsStore, type DraftResult } from '../stores/output_skills'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only page (gated at the router) to run a memory-inheriting
// output skill. Picks a skill from the installed catalog,
// binds it to a target (+ optional notes), POSTs to the draft endpoint,
// and renders the returned draft + citations. The result is transient —
// the owner reviews/edits/sends it; nothing is written to the wiki.
const store = useOutputSkillsStore()
const message = useMessage()
useDocumentTitle('Output skills')

// Plain markdown-it (NOT the wiki renderer): the draft is a client
// deliverable that may mention `*.md`-looking text but isn't part of the
// wiki graph, so we don't want the wiki renderer minting dead `/p/…`
// links. Citations are rendered separately as real links below.
const md = new MarkdownIt({ html: false, linkify: false, breaks: false })

const selectedSkill = ref<string | null>(null)
const target = ref('')
const notes = ref('')
const submitting = ref(false)
const result = ref<DraftResult | null>(null)

const skillOptions = computed(() => store.skills.map((s) => ({ label: `${s.slug} — ${s.description}`, value: s.slug })))

const canSubmit = computed(() => !!selectedSkill.value && target.value.trim().length > 0 && !submitting.value)

const renderedDraft = computed(() => (result.value ? md.render(result.value.draft) : ''))

async function submit() {
  if (!canSubmit.value || !selectedSkill.value) return
  submitting.value = true
  try {
    result.value = await store.draftOutput(selectedSkill.value, target.value.trim(), notes.value)
  } catch (err) {
    message.error(err instanceof Error ? err.message : 'Failed to draft')
  } finally {
    submitting.value = false
  }
}

function reset() {
  result.value = null
}

onMounted(() => {
  if (!store.loaded) void store.fetchSkills()
})
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-6 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="page-title">Output skills</h1>
        <p class="text-sm text-white/60 m-0">
          Run a memory-inheriting skill to draft a deliverable for a target. The draft is grounded in what the brain
          knows about the target, with citations — review and edit it before sending. Nothing is written to the wiki.
        </p>
      </header>

      <!-- Catalog lifecycle: first-load spinner → error → empty. Keyed on
           `loading && !loaded` so a failed fetch lights the error branch
           instead of trapping a perpetual spinner. -->
      <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <div v-else-if="store.loadError" class="text-red-300 text-sm" data-testid="output-skills-load-error">
        Failed to load skills: {{ store.loadError }}
      </div>

      <NEmpty v-else-if="store.isEmpty" class="my-12" data-testid="output-skills-empty">
        <template #default>
          <p class="text-sm text-white/60 m-0 max-w-md mx-auto">
            No output skills installed yet. Skills are configured via the API or by running the bundled seeds — see the
            <a
              href="https://github.com/mariusog/superbrain/blob/main/docs/reference/api/output-skills.md"
              target="_blank"
              rel="noopener noreferrer"
              class="text-brand-primary hover:underline"
              data-testid="output-skills-docs-link"
              >documentation</a
            >.
          </p>
        </template>
      </NEmpty>

      <!-- Run form -->
      <form v-else-if="!result" class="flex flex-col gap-4" data-testid="output-skill-form" @submit.prevent="submit">
        <label class="flex flex-col gap-1 text-sm text-white/70">
          <span>Skill</span>
          <NSelect
            v-model:value="selectedSkill"
            :options="skillOptions"
            placeholder="Choose an output skill"
            data-testid="output-skill-select"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm text-white/70">
          <span>Target</span>
          <NInput
            v-model:value="target"
            placeholder="Who/what the deliverable is about — a client, project, or person"
            data-testid="output-skill-target"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm text-white/70">
          <span>Notes (optional)</span>
          <NInput
            v-model:value="notes"
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 12 }"
            placeholder="This-period specifics the wiki may not record yet — what shipped, what's blocked"
            data-testid="output-skill-notes"
          />
        </label>

        <div class="flex justify-end">
          <NButton
            type="primary"
            :disabled="!canSubmit"
            :loading="submitting"
            data-testid="output-skill-draft"
            @click="submit"
          >
            Draft
          </NButton>
        </div>
      </form>

      <!-- Draft result -->
      <section v-else class="flex flex-col gap-4" data-testid="output-skill-result">
        <div class="flex items-center justify-between gap-4">
          <h2 class="text-lg font-semibold text-white m-0">{{ result.skill }} → {{ result.target }}</h2>
          <NButton size="small" data-testid="output-skill-reset" @click="reset"> Draft another </NButton>
        </div>

        <div
          v-if="result.weak_citation"
          class="text-amber-300 text-sm border border-amber-400/30 rounded px-3 py-2"
          data-testid="output-skill-weak"
        >
          ⚠️ Thin memory for this target — this draft is under-grounded. Verify against sources before sending.
        </div>

        <article class="wiki-markdown text-sm" v-html="renderedDraft" />

        <section v-if="result.citations.length" class="flex flex-col gap-1">
          <h3 class="text-sm font-semibold text-white/80 m-0">Citations</h3>
          <ul class="m-0 pl-4 text-sm text-white/70 flex flex-col gap-1">
            <li v-for="(c, i) in result.citations" :key="c.path">
              [{{ i + 1 }}]
              <RouterLink :to="`/p/${c.path}`" class="text-brand-primary">
                {{ c.title || c.path }}
              </RouterLink>
            </li>
          </ul>
        </section>
      </section>
    </section>
  </RouteShell>
</template>
